import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

interface Props {
  orgSlug: string;
  projectSlug: string;
}

interface HealthResponse {
  reachable: boolean;
  checkedAt: string;
  url: string;
  statusCode?: number;
  elapsedMs?: number;
}

/**
 * v4 (004-project-overview-tab) — live-status dot for the Production card.
 *
 * Polls GET /health every 30s. Server caches results in Redis for 30s so
 * the actual probe to the customer's site runs at most every 30s regardless
 * of concurrent viewers. The combined refresh cadence (server 30s cache +
 * client 30s poll) gives the SC-004 60s "reflects external change within
 * 60s" bound.
 */
export function LiveStatusDot({ orgSlug, projectSlug }: Props) {
  const health = useQuery({
    queryKey: ["project-health", orgSlug, projectSlug],
    queryFn: () => api.get<HealthResponse>(`/orgs/${orgSlug}/projects/${projectSlug}/health`),
    refetchInterval: 30_000,
    staleTime: 25_000,
  });

  const tone = health.isLoading
    ? "bg-attention border-attention/50"
    : health.data?.reachable
      ? "bg-success border-success/50"
      : "bg-danger border-danger/50";

  const tooltip = health.isLoading
    ? "Checking…"
    : health.data?.reachable
      ? `Reachable · ${health.data.url}${health.data.statusCode ? ` · ${health.data.statusCode}` : ""}`
      : `Could not reach ${health.data?.url ?? "(unknown)"}`;

  return (
    <span
      title={tooltip}
      aria-label={tooltip}
      className={cn(
        "inline-block h-2.5 w-2.5 rounded-full border",
        tone,
        health.data?.reachable === false && "animate-pulse-dot",
      )}
    />
  );
}
